/**
 * DrawerStackContext — gestion centralisée d'une pile de `<Drawer>` (S5b).
 *
 * Pourquoi : un drawer ouvert depuis un autre drawer doit savoir qu'il
 * empile par-dessus (élévation visuelle + Escape ferme le sommet, pas
 * tout). Sans ce contexte, chaque `<Drawer>` se retrouverait à inventer
 * son propre z-index ad-hoc et l'Escape global fermerait toute la pile.
 *
 * **Verrou ESLint** `drawer-must-use-stack` : tout `<Drawer>` doit être
 * monté à l'intérieur d'un `<DrawerStackProvider>`.
 *
 * Ce module exporte uniquement `createContext` + le hook — la JSX provider
 * vit dans `DrawerStackContext.tsx` (séparation Fast Refresh).
 */
import { createContext, useContext } from 'react'

export interface DrawerStackContextValue {
  /** Index de l'instance Drawer dans la pile (0 = bottom, n = top). */
  register: () => number
  /** Retire l'instance — appelé en cleanup d'effet. */
  unregister: (index: number) => void
  /** Profondeur courante — drives z-index + offset visuel. */
  depth: number
}

export const DrawerStackContext = createContext<DrawerStackContextValue | null>(null)

export function useDrawerStack(): DrawerStackContextValue {
  const ctx = useContext(DrawerStackContext)
  if (!ctx) {
    throw new Error(
      'useDrawerStack must be used within a <DrawerStackProvider>. ' +
        'Wrap your App or Page in <DrawerStackProvider>.',
    )
  }
  return ctx
}
